/**
 * useUpgrade Hook
 * 
 * Centralized upgrade/subscription state management.
 * Determines if user is Pro, shows upgrade modals, handles plan selection.
 * 
 * Uses Checkout API with external_customer_id for reliable user identification.
 * See POLAR_LOG.md for full documentation.
 */

import { useState, useCallback, useMemo } from 'react';
import { useAuth } from '@services/auth';
import { PricingPlan } from '@/components/upgrade/shared/constants';

// API base URL - use relative for same-origin, or explicit for cross-origin
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.ninjacash.app';

// Owner emails - can access /dev page
const OWNER_EMAILS = ['nova223@gmail.com', 'teslax707@gmail.com'];
// Pro bypass - only these get free Pro (others must pay)
const PRO_BYPASS_EMAILS = ['nova223@gmail.com'];

export type SubscriptionStatus = 'free' | 'trialing' | 'active' | 'past_due' | 'cancelled' | 'lifetime';

export interface UseUpgradeReturn {
  // Subscription state
  isPro: boolean;
  subscriptionStatus: SubscriptionStatus;
  isOwner: boolean;
  
  // Modal state
  showUpgradeModal: boolean;
  upgradeModalTrigger: 'sync-toggle' | 'second-device' | '30-day-prompt';
  openUpgradeModal: (trigger?: 'sync-toggle' | 'second-device' | '30-day-prompt') => void;
  closeUpgradeModal: () => void;
  
  // Plan selection
  handleSelectPlan: (plan: PricingPlan) => void;
  isCheckoutLoading: boolean;
}

export function useUpgrade(): UseUpgradeReturn {
  const { user, session } = useAuth();
  
  // Modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalTrigger, setUpgradeModalTrigger] = useState<'sync-toggle' | 'second-device' | '30-day-prompt'>('sync-toggle');
  
  // Check if user is owner (always has Pro)
  const isOwner = useMemo(() => {
    if (!user?.email) return false;
    return OWNER_EMAILS.includes(user.email.toLowerCase());
  }, [user?.email]);
  
  // Get subscription status from user metadata
  const subscriptionStatus = useMemo((): SubscriptionStatus => {
    if (!user) return 'free';
    
    // Check user_metadata for subscription status
    const status = user.user_metadata?.subscriptionStatus;
    if (status && ['trialing', 'active', 'past_due', 'cancelled', 'lifetime'].includes(status)) {
      return status as SubscriptionStatus;
    }
    
    return 'free';
  }, [user]);
  
  // Determine if user has Pro access
  const isPro = useMemo(() => {
    // Only PRO_BYPASS_EMAILS get free Pro (not all owners)
    if (user?.email && PRO_BYPASS_EMAILS.includes(user.email.toLowerCase())) return true;
    
    // Check subscription status (trialing also grants Pro)
    return subscriptionStatus === 'active' || subscriptionStatus === 'lifetime' || subscriptionStatus === 'trialing';
  }, [user?.email, subscriptionStatus]);
  
  // Open upgrade modal with specified trigger
  const openUpgradeModal = useCallback((trigger: 'sync-toggle' | 'second-device' | '30-day-prompt' = 'sync-toggle') => {
    setUpgradeModalTrigger(trigger);
    setShowUpgradeModal(true);
    console.log('[useUpgrade] Modal opened, trigger:', trigger);
  }, []);
  
  // Close upgrade modal
  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
    console.log('[useUpgrade] Modal closed');
  }, []);
  
  // State for checkout loading
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  // Handle plan selection - create checkout via API and redirect
  // Uses Checkout API with external_customer_id for reliable user identification
  const handleSelectPlan = useCallback(async (plan: PricingPlan) => {
    console.log('[useUpgrade] Plan selected:', plan);
    
    if (!session?.access_token) {
      console.error('[useUpgrade] No session token available');
      return;
    }

    if (plan !== 'monthly' && plan !== 'lifetime') {
      console.error('[useUpgrade] Unknown plan:', plan);
      return;
    }

    setIsCheckoutLoading(true);

    try {
      // Call API to create checkout session
      // API uses external_customer_id to link payment to user (works even if user pays with different email)
      const response = await fetch(`${API_BASE_URL}/api/checkout/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ plan }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[useUpgrade] Checkout API error:', response.status, errorData);
        // TODO: Show user-friendly error message
        setIsCheckoutLoading(false);
        return;
      }

      const { checkoutUrl, checkoutId } = await response.json();
      
      if (!checkoutUrl) {
        console.error('[useUpgrade] No checkoutUrl in response');
        setIsCheckoutLoading(false);
        return;
      }

      // Store checkoutId for instant verification on return
      // This enables bypassing the ~60s webhook delay
      if (checkoutId) {
        localStorage.setItem('ninjacash_pending_checkout', JSON.stringify({
          checkoutId,
          startedAt: Date.now(),
        }));
        console.log('[useUpgrade] Stored checkoutId for verification:', checkoutId);
      }

      console.log('[useUpgrade] Redirecting to Polar checkout:', checkoutUrl);
      
      // Close modal and redirect
      setShowUpgradeModal(false);
      window.location.href = checkoutUrl;
      
    } catch (err) {
      console.error('[useUpgrade] Failed to create checkout:', err);
      setIsCheckoutLoading(false);
      // TODO: Show user-friendly error message
    }
  }, [session?.access_token]);
  
  return {
    isPro,
    subscriptionStatus,
    isOwner,
    showUpgradeModal,
    upgradeModalTrigger,
    openUpgradeModal,
    closeUpgradeModal,
    handleSelectPlan,
    isCheckoutLoading,
  };
}
